import { useCallback, useEffect, useMemo, useState } from 'react';
import { PODIUMAUTHORS } from '../../../core/constants';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { BlockAuthor, Exclude, Watch } from '../../../composed-components/Actions';
import { BASEFONTSIZE, COLUMNSHEADING, PAGESIZE, REFETCHDELAY, RefetchOptions } from '../constants';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import { EFontSize, IBook, TColumns } from '../types/types';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import { useUserPermit } from '../../../context/User.context';
import { useActions } from '../../../context/Actions.context';
import { TableFooter } from '../../../components/Table';
import getAllColumns from '../columns/getAllColumns';
import ResizeTable from '../../../composed-components/Resize';
import Pagination from '../../../components/Pagination';

const PodiumAuthors = () => {
  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);
  const [deleteItems, setDeleteItems] = useState<string[]>([]);

  const { edit_titles, move_titles_hubspot } = useUserPermit();
  const { isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { filter } = useFilter();
  const { sort } = useSort();

  const changePage = p => setPage(p);
  const deleteBooks = book => setDeleteItems(book);
  const changeFontSize = size => setFontSize(size);

  const { data = [], isLoading, isFetching, refetch } = useBooksList(
    PODIUMAUTHORS, {
      page,
      ...filter[PODIUMAUTHORS],
      ...sort[PODIUMAUTHORS]
    });

  const {
    count,
    isFetching: isCountFetching,
    refetch: refetchRowCount
  } = useDataRowCount(PODIUMAUTHORS, { ...filter[PODIUMAUTHORS] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortComponent = useCallback(
    (title: string) => (
      <Sorting id={PODIUMAUTHORS} title={title} changePage={changePage} />
    ),
    []
  );

  const thead = useMemo(() => {
    const allColumns = getAllColumns(sortComponent);
    const head = COLUMNSHEADING[PODIUMAUTHORS].map(title => allColumns[title]);

    if (edit_titles) {
      head.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className='actions'>
            <BlockAuthor book={book} id={PODIUMAUTHORS} />
            <Watch book={book} id={PODIUMAUTHORS} />
            <Exclude book={book} id={PODIUMAUTHORS} />
          </div>
        )
      });
    }

    return head;
  }, [edit_titles, move_titles_hubspot, sortComponent]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource: IBook[] | undefined = data?.map(book => ({
    ...book,
    key: book['Author Id']
  }));

  useEffect(() => {
    if (!isRefreshed) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }
  }, [page, filter[PODIUMAUTHORS], sort[PODIUMAUTHORS]]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm id={PODIUMAUTHORS} page={page} changePage={changePage} />
        <Settings
          thead={thead}
          id={PODIUMAUTHORS}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteBooks={deleteBooks}
          deleteItems={deleteItems}
        />
      </div>
      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            id={PODIUMAUTHORS}
            dataSource={dataSource}
            deleteItems={deleteItems}
            isFetching={isFetching || isActionLoading}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter>
            <Pagination
              total={count}
              isCountLoading={isCountFetching}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default PodiumAuthors;